import { cn } from '@repo/lib/cn';
import { questionTranslateSchema } from '@repo/schemas/quiz/questionTranslate';
import { CheckIcon, DeleteIcon, XIcon } from 'lucide-react';

import { QuestionContentFormField, QuestionTranslateFormAnswers, QuestionTranslateFormOption } from '@/components/features/quiz/question/question-builder';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button/ButtonGroup';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { ErrorMessage, Form } from '@/components/ui/form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form/Form';
import { Input } from '@/components/ui/input';
import { Kbd } from '@/components/ui/kbd';
import { Textarea } from '@/components/ui/textarea';
import { useQuestionTranslateForm } from '@/hooks/questions';
import { formErrorToast } from '@/utils/form';

import type { QuestionTranslateFormProps } from '.';

export function QuestionTranslateForm(questionTranslateFormProps: QuestionTranslateFormProps) {
  const { data, onSubmit = () => {}, className, ...props } = questionTranslateFormProps;

  const {
    form,
    inputValue,
    selectedOptions,
    isAddControlsDisabled,
    translateQuestionOptions,
    translateQuestionAnswers,
    deleteOptionAlert,
    handleAddOption,
    handleRemoveOption,
    handleRemoveSelectedOption,
    handleSelectOption,
    handleCreateAnswer,
    handleClearSelectedOptions,
    handleCopyAnswer,
    handleInputChange,
    handleConfirmDeleteOption,
    handleCancelDeleteOption,
  } = useQuestionTranslateForm(data);

  return (
    <Form {...form}>
      <AlertDialog open={deleteOptionAlert.isOpen} onOpenChange={(open) => !open && handleCancelDeleteOption()}>
        <AlertDialogContent className={'sm:max-w-96'}>
          <AlertDialogBody>
            <AlertDialogHeader>
              <div className={'flex min-w-0 grow flex-col gap-1'}>
                <AlertDialogTitle>Usuń blok tekstu</AlertDialogTitle>
                <AlertDialogDescription>
                  Ten blok tekstu jest używany w zapisanych kombinacjach. Usunięcie go spowoduje również usunięcie wszystkich kombinacji, które go zawierają. Czy na pewno chcesz kontynuować?
                </AlertDialogDescription>
              </div>
            </AlertDialogHeader>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDeleteOption}>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeleteOption}>Usuń mimo to</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <form {...props} noValidate onSubmit={form.handleSubmit(onSubmit, () => formErrorToast(form, questionTranslateSchema))} className={cn('h-full', className)}>
        <div className="divide-border-primary divide-y">
          <QuestionContentFormField />
          <div className="space-y-12 p-3">
            <div className="space-y-3">
              <div className="flex gap-3">
                <Input
                  placeholder="Wprowadź blok tekstu"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button variant={'outline'} type={'button'} onClick={handleAddOption} disabled={isAddControlsDisabled}>
                  Dodaj <Kbd>⏎</Kbd>
                </Button>
              </div>
              <div className="border-border-primary bg-accent/75 flex flex-wrap gap-2 rounded-lg border border-dashed p-2">
                <ErrorMessage className="my-4 min-h-0 w-full text-center" name={'options'} />

                {translateQuestionOptions.fields.map((option, optionIndex) => (
                  <QuestionTranslateFormOption key={option.id} optionIndex={optionIndex} onRemoveOption={handleRemoveOption} onSelectOption={handleSelectOption} />
                ))}
              </div>
              <p className="text-muted-foreground text-sm">💡 Kliknij na bloki wyrazów, aby dodać je do bieżącej kombinacji</p>
            </div>
            <div className="space-y-3">
              <h5 className="text-accent-foreground text-sm">Utwórz kombinację:</h5>
              <div className={'border-border-primary bg-accent/75 flex flex-wrap gap-2 rounded-lg border border-dashed p-2'}>
                {selectedOptions.length === 0 && <span className="text-muted-foreground w-full py-4 text-center text-sm">Brak wybranych bloków</span>}
                {selectedOptions.map((option, index) => (
                  <ButtonGroup key={option.id}>
                    <Button variant="outline" size="sm" type="button">
                      {option.content}
                    </Button>
                    <Button variant="outline" size="sm" type="button" onClick={() => handleRemoveSelectedOption(index)}>
                      <XIcon />
                    </Button>
                  </ButtonGroup>
                ))}
              </div>
              <div className="flex gap-3">
                <Button type="button" variant={'tertiary'} disabled={!selectedOptions.length || translateQuestionAnswers.fields.length === 5} onClick={handleCreateAnswer}>
                  <CheckIcon />
                  Zapisz kombinację
                </Button>
                <Button type="button" variant={'outline'} disabled={!selectedOptions.length || translateQuestionAnswers.fields.length === 5} onClick={handleClearSelectedOptions}>
                  <DeleteIcon />
                  Wyczyść
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">Limit kombinacji: {translateQuestionAnswers.fields.length}/5</p>
            </div>
            <div className="space-y-3">
              <h5 className="text-accent-foreground text-sm">Zapisane kombinacje:</h5>
              {translateQuestionAnswers.fields.map((answer, answerIndex) => (
                <QuestionTranslateFormAnswers key={answer.fieldId} answerIndex={answerIndex} onRemove={translateQuestionAnswers.remove} onCopy={handleCopyAnswer} />
              ))}
              <ErrorMessage className="min-h-0 w-full" name={'answers'} />
            </div>
            <FormField
              name={'explanation'}
              control={form.control}
              render={({ field }) => (
                <FormItem className={'w-full'}>
                  <FormLabel>
                    Wyjaśnienie <small className={'text-muted-foreground text-sm'}>(opcionalne)</small>
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder={'Wprowadź wyjaśnienie...'} {...field} value={field.value || ''} onChange={(e) => field.onChange(e.target.value || undefined)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}
